import { type HTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/utils';
import { cardVariants, type CardProps } from './variants';

/**
 * Generic card container for content sections.
 *
 * Accepts any children and renders them inside a styled container.
 * Compose with `CardHeader`, `CardContent`, and `CardFooter` for
 * structured layouts.
 *
 * @example
 * ```tsx
 * <Card>
 *   <CardHeader>
 *     <EmptyCircle src={url} alt="Question" />
 *     <h3>Question Title</h3>
 *   </CardHeader>
 *   <CardContent>…options…</CardContent>
 *   <CardFooter>$ 1,469,300 Vol</CardFooter>
 * </Card>
 * ```
 */
const Card = forwardRef<HTMLDivElement, CardProps>(({ className, variant, ...props }, ref) => (
  <div ref={ref} className={cn(cardVariants({ variant }), className)} {...props} />
));
Card.displayName = 'Card';

/**
 * Header section of a card — typically holds a thumbnail, title, and subtitle.
 */
const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('flex items-start gap-2.5 p-3.5 pb-0', className)} {...props} />
));
CardHeader.displayName = 'CardHeader';

/**
 * Main body of a card — holds option rows, charts, or any primary content.
 */
const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('flex flex-col gap-1 px-3.5 pt-2.5', className)} {...props} />
));
CardContent.displayName = 'CardContent';

/**
 * Footer bar for a card — renders with a top border and muted background.
 *
 * Commonly used for volume info and market links.
 */
const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'flex items-center justify-between px-3.5 py-2.5 mt-2',
      'border-t border-border bg-surface-muted',
      'rounded-b-[15px]',
      className,
    )}
    {...props}
  />
));
CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardContent, CardFooter };
